import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Slider-style product gallery component
 * 
 * @typedef {object} Refs
 * @property {import('./slideshow').Slideshow} slideshow - The slideshow component
 * @property {HTMLElement[]} [thumbnails] - Thumbnail buttons
 * @property {import('./slider-zoom-dialog').SliderZoomDialog} [zoomDialog] - Zoom dialog component
 * 
 * @extends Component<Refs>
 */
export class SliderGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    
    this.#setupThumbnails();
    this.#setupSlideshowListener();
    this.#setupAspectRatioHandler();
    this.#setupImageClickHandler();
    
    // Initialize active thumbnail state
    this.#initializeActiveThumbnail();
    
    // Initialize badge slider visibility
    this.#badgeSlider = this.closest('.main-product__media')?.querySelector('.badge-slider') || null;
    this.#updateBadgeSliderVisibility();
  }

  #controller = new AbortController();
  #isDragging = false;

  /**
   * ResizeObserver instance for tracking window size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * Cached badge slider element
   * @type {HTMLElement | null}
   */
  #badgeSlider = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    
    // Удаляем обработчик resize
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  /**
   * Setup thumbnail click handlers and sync
   */
  #setupThumbnails() {
    const { thumbnails } = this.refs;
    if (!thumbnails?.length) return;

    const thumbsContainer = this.querySelector('.slider-gallery__thumbs');
    if (!thumbsContainer || !(thumbsContainer instanceof HTMLElement)) return;

    // Setup drag scrolling for thumbnails container
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    // Prevent native image drag inside thumbnails
    thumbsContainer.addEventListener('dragstart', (e) => e.preventDefault());

    // The side thumbs column scrolls vertically on its own, the drag and wheel handlers below are horizontal
    if (this.classList.contains('slider-gallery--side-thumbs')) return;

    const handleMouseDown = (/** @type {MouseEvent} */ e) => {
      if (!(thumbsContainer instanceof HTMLElement)) return;
      e.preventDefault();
      isDown = true;
      this.#isDragging = false;
      thumbsContainer.classList.add('slider-gallery__thumbs--dragging');
      startX = e.pageX - thumbsContainer.offsetLeft;
      scrollLeft = thumbsContainer.scrollLeft;
    };

    const handleMouseUp = () => {
      isDown = false;
      thumbsContainer.classList.remove('slider-gallery__thumbs--dragging');
    };

    const handleMouseMove = (/** @type {MouseEvent} */ e) => {
      if (!isDown || !(thumbsContainer instanceof HTMLElement)) return;
      e.preventDefault();
      this.#isDragging = true;
      const x = e.pageX - thumbsContainer.offsetLeft;
      const walk = (x - startX) * 2;
      thumbsContainer.scrollLeft = scrollLeft - walk;
    };

    thumbsContainer.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);

    // Setup wheel scrolling for thumbnails container
    thumbsContainer.addEventListener('wheel', (/** @type {WheelEvent} */ e) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        thumbsContainer.scrollLeft += e.deltaY;
      }
    });
  }

  /**
   * Initialize active thumbnail state
   */
  #initializeActiveThumbnail() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    // Get current active slide index from slideshow
    const activeIndex = slideshow.current ?? 0;
    this.#updateActiveThumbnail(activeIndex);
  }

  /**
   * Listen to slideshow events to sync thumbnails
   */
  #setupSlideshowListener() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const { signal } = this.#controller;
    
    slideshow.addEventListener('slideshow:select', (/** @type {CustomEvent} */ event) => {
      const { index } = event.detail;
      this.#updateActiveThumbnail(index);
      this.#updateBadgeSliderVisibility(index);
      // Not inside #updateActiveThumbnail: it returns early when thumbnails are hidden
      this.#updateAspectRatio(index);
    }, { signal });
  }

  /**
   * Update active thumbnail state
   * @param {number} index - Index of the active thumbnail
   */
  #updateActiveThumbnail(index) {
    const { thumbnails } = this.refs;
    if (!thumbnails?.length) return;

    thumbnails.forEach((thumb, i) => {
      const isActive = i === index;
      thumb.classList.toggle('slider-gallery__thumb--active', isActive);
      thumb.setAttribute('aria-selected', String(isActive));
    });

    // Scroll active thumbnail into view
    this.#scrollThumbnailIntoView(index);
  }

  /**
   * Scroll thumbnail into view if it's not visible
   * @param {number} index - Index of the thumbnail to scroll
   */
  #scrollThumbnailIntoView(index) {
    const { thumbnails } = this.refs;
    if (!thumbnails?.length) return;

    const activeThumbnail = thumbnails[index];
    if (!activeThumbnail) return;

    // The side thumbs layout scrolls its inner column vertically
    if (this.classList.contains('slider-gallery--side-thumbs')) {
      const column = activeThumbnail.closest('.slider-gallery__thumbs-scroller');
      if (!column) return;

      const columnRect = column.getBoundingClientRect();
      const thumbRect = activeThumbnail.getBoundingClientRect();

      if (thumbRect.top < columnRect.top || thumbRect.bottom > columnRect.bottom) {
        column.scrollTo({
          top: column.scrollTop + thumbRect.top - columnRect.top - (columnRect.height - thumbRect.height) / 2,
          behavior: 'smooth',
        });
      }

      return;
    }

    const thumbsContainer = activeThumbnail.closest('.slider-gallery__thumbs');
    if (!thumbsContainer) return;

    // Get container and thumbnail dimensions
    const containerRect = thumbsContainer.getBoundingClientRect();
    const thumbnailRect = activeThumbnail.getBoundingClientRect();

    // Check if thumbnail is partially or fully outside container
    const isOutOfView = 
      thumbnailRect.left < containerRect.left || 
      thumbnailRect.right > containerRect.right;

    if (isOutOfView) {
      // Calculate scroll position to center the thumbnail
      const scrollLeft = activeThumbnail.offsetLeft - (containerRect.width / 2) + (thumbnailRect.width / 2);
      
      // Smooth scroll to the thumbnail
      thumbsContainer.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Select a slide by index
   * @param {number} index - Slide index
   * @param {Event} [event] - Event that triggered the selection
   */
  selectSlide(index, event) {
    if (this.#isDragging) return;

    const { slideshow } = this.refs;
    if (!slideshow) return;

    // Use scroll animation with reorder disabled to prevent flickering
    slideshow.select(index, event, { disableReorder: true });
  }

  /**
   * Setup click handler on slide images to open zoom
   */
  #setupImageClickHandler() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const { signal } = this.#controller;

    slideshow.addEventListener('click', (/** @type {MouseEvent} */ e) => {
      const img = /** @type {HTMLElement} */ (e.target);
      if (!img.closest('.slider-gallery__media-wrap')) return;

      const slide = img.closest('slideshow-slide');
      if (!slide) return;

      const index = parseInt(slide.getAttribute('data-index') || '0', 10);
      this.openZoom(index);
    }, { signal });
  }

  /**
   * Open zoom dialog at specified slide index
   * @param {number} index
   */
  openZoom(index) {
    const { zoomDialog } = this.refs;
    if (!zoomDialog) return;
    zoomDialog.open(index);
  }

  /**
   * Handle variant update event
   * @param {CustomEvent} event - Variant update event
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newGallery = source.querySelector('slider-gallery');
    if (!newGallery) return;

    this.replaceWith(newGallery);
  };

  /**
   * Setup aspect ratio handler for desktop
   */
  #setupAspectRatioHandler() {
    // Создаем ResizeObserver для отслеживания изменения ширины окна
    this.#resizeObserver = new ResizeObserver(() => {
      const { slideshow } = this.refs;
      if (slideshow) {
        const currentIndex = slideshow.current ?? 0;
        this.#updateAspectRatio(currentIndex);
      }
    });

    // Наблюдаем за элементом body для отслеживания изменений размера окна
    if (document.body) {
      this.#resizeObserver.observe(document.body);
    }

    // Инициализация при первой загрузке
    const { slideshow } = this.refs;
    if (slideshow) {
      const currentIndex = slideshow.current ?? 0;
      this.#updateAspectRatio(currentIndex);
    }
  }

  /**
   * Update aspect ratio on scroller for desktop (750px+)
   * @param {number} slideIndex - Index of the current slide
   */
  #updateAspectRatio(slideIndex) {
    const isDesktop = this.#checkDesktopWidth();
    const isMobileAdaptive = this.classList.contains('slider-gallery--mobile-adaptive');

    if (!isDesktop && !isMobileAdaptive) {
      // Удаляем aspect-ratio если не desktop
      this.#setScrollerAspectRatio(null);
      return;
    }

    const { slideshow } = this.refs;
    if (!slideshow || !slideshow.refs?.slides) return;
    const currentSlide = slideshow.refs.slides[slideIndex];
    if (!currentSlide) return;
    const aspectRatio = currentSlide.getAttribute(isDesktop ? 'data-aspect-ratio' : 'data-mobile-slide-aspect-ratio');
    
    if (aspectRatio) {
      this.#setScrollerAspectRatio(aspectRatio);
    }
  }

  /**
   * Check if current viewport width is 750px or more
   * @returns {boolean} True if desktop width
   */
  #checkDesktopWidth() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  /**
   * Set aspect ratio style on scroller element
   * @param {string|null} aspectRatio - Aspect ratio value or null to remove
   */
  #setScrollerAspectRatio(aspectRatio) {
    const { slideshow } = this.refs;
    if (!slideshow || !slideshow.refs?.scroller) return;
    
    const scroller = slideshow.refs.scroller;

    if (scroller instanceof HTMLElement) {
      if (aspectRatio) {
        scroller.style.aspectRatio = aspectRatio;
      } else {
        scroller.style.removeProperty('aspect-ratio');
      }
    }
  }

  /**
   * Update badge slider visibility based on current slide
   * @param {number} index - Index of the active thumbnail
   */
  #updateBadgeSliderVisibility(index = 0) {
    if (!this.#badgeSlider) return;
    
    this.#badgeSlider.classList.toggle('hidden', index !== 0);
  }
}

if (!customElements.get('slider-gallery')) {
  customElements.define('slider-gallery', SliderGallery);
}
